import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import { useIsTVDevice } from '../hooks/useTVNavigation';

export const Route = createFileRoute('/tv-activate')({
  component: TVActivatePage,
});

function TVActivatePage() {
  const [code, setCode] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isActivated, setIsActivated] = useState(false);
  
  const { initiateDeviceActivation, checkActivationStatus, completeDeviceActivation } = useAuthStore();
  const isTV = useIsTVDevice();
  const navigate = useNavigate();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generate activation code when page loads
  useEffect(() => {
    generateCode();

    return () => {
      // Cleanup on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const generateCode = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const activation = await initiateDeviceActivation();
      setCode(activation.code);
      setExpiresAt(new Date(activation.expiresAt));
      setTimeRemaining(600); // 10 minutes in seconds
      
      // Start polling for activation
      startPolling(activation.code);
      
      // Start countdown timer
      startCountdown();
    } catch (err: any) {
      setError(err.message || 'Failed to generate activation code');
    } finally {
      setIsLoading(false);
    }
  };

  const startCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startPolling = useCallback((activationCode: string) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const status = await checkActivationStatus(activationCode);
        
        if (status.status === 'activated' && status.sessionToken && status.refreshToken) {
          // Activation successful
          clearInterval(pollIntervalRef.current!);
          clearInterval(countdownIntervalRef.current!);
          
          setIsActivated(true);
          
          // Complete activation and set session
          await completeDeviceActivation(activationCode, status.sessionToken, status.refreshToken);
          
          // Wait a moment for session to be set, then redirect
          setTimeout(() => {
            navigate({ to: '/' });
          }, 1000);
        } else if (status.status === 'expired') {
          clearInterval(pollIntervalRef.current!);
          clearInterval(countdownIntervalRef.current!);
          setError('Activation code has expired. Please try again.');
        }
      } catch (err: any) {
        console.error('Error polling activation status:', err);
        // Don't set error for polling errors, just log them
      }
    }, 2000); // Poll every 2 seconds
  }, [checkActivationStatus, completeDeviceActivation, navigate]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleBack = () => {
    navigate({ to: '/' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl w-full"
      >
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="mb-8 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          tabIndex={isTV ? 0 : undefined}
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        {/* Main Content */}
        <div className="bg-[#141414] rounded-lg border border-gray-800 p-8 md:p-12 shadow-2xl">
          {isActivated ? (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <svg
                    className="w-12 h-12 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </motion.div>
              <h2 className="text-4xl font-bold text-white mb-4">Activation Successful!</h2>
              <p className="text-xl text-gray-400 mb-6">You are now signed in.</p>
              <p className="text-sm text-gray-500">Redirecting to home...</p>
              <Loader2 className="w-8 h-8 text-red-600 animate-spin mx-auto mt-6" />
            </div>
          ) : isLoading && !code ? (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-2">Generating Activation Code...</h2>
            </div>
          ) : code ? (
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Sign in on your phone
              </h1>
              
              <p className="text-xl text-gray-400 mb-8">
                Go to <span className="text-red-600 font-semibold">moviesvault.info/activate</span> on your phone
                <br />
                and enter this code:
              </p>

              {/* Activation Code Display */}
              <div className="mb-10">
                <div className="inline-flex items-center gap-4 px-12 py-8 bg-black/50 rounded-lg border-2 border-gray-700">
                  {code.split('').map((digit, index) => (
                    <motion.span
                      key={index}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="text-7xl md:text-8xl font-bold text-white tracking-wider"
                    >
                      {digit}
                    </motion.span>
                  ))}
                </div>
              </div>

              {/* Timer */}
              <div className="mb-8">
                <p className="text-gray-500 text-lg mb-3">Code expires in:</p>
                <p className="text-4xl font-mono font-bold text-red-600">
                  {formatTime(timeRemaining)}
                </p>
              </div>

              {/* Instructions */}
              <div className="mt-12 pt-8 border-t border-gray-800">
                <h3 className="text-xl font-semibold text-white mb-4">Instructions:</h3>
                <ol className="text-left max-w-md mx-auto space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">1</span>
                    <span>Open your phone's web browser</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">2</span>
                    <span>Go to <span className="text-red-600 font-semibold">moviesvault.info/activate</span></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">3</span>
                    <span>Enter the code shown above</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">4</span>
                    <span>Sign in with your Google account</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">5</span>
                    <span>This device will automatically sign in</span>
                  </li>
                </ol>
              </div>

              {/* Status */}
              <div className="mt-8 flex items-center justify-center gap-3 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-lg">Waiting for activation...</span>
              </div>

              {error && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg max-w-md mx-auto">
                  <p className="text-red-400 text-sm mb-3">{error}</p>
                  <button
                    onClick={generateCode}
                    className="text-red-600 hover:text-red-500 text-sm underline"
                    tabIndex={isTV ? 0 : undefined}
                  >
                    Generate new code
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Device Activation</h2>
              <button
                onClick={generateCode}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                tabIndex={isTV ? 0 : undefined}
              >
                Generate Code
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

